'use client';

import { c as _c } from "react/compiler-runtime";
import { DeleteOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CelebritySearchSelect } from '@/components/atoms/CelebritySearchSelect';
import { AppliedFilters } from '@/components/atoms/FilterActions';
import type { AppliedFilter } from '@/components/atoms/FilterActions/AppliedFilters';
import { celebritiesListOptions } from '@/entities/Celebrity/api/celebritiesList';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { SortRadioButton } from '@/shared/components/Sort/SortRadioButton';
import { SortRadioGroup } from '@/shared/components/Sort/SortRadioGroup';
import styles from './FilterCelebrities.module.css';
export interface FilterCelebritiesProps {
  /** Выбранные ID знаменитостей */
  celebrities?: number[];
  /** Обработчик изменения выбранных знаменитостей */
  onCelebritiesChange?: (ids: number[]) => void;
  /** Текст поиска внутри селекта (контролируемый, как в FilterExperiences) */
  celebrityName?: string;
  /** Установка текста поиска */
  setCelebrityName?: (value: string) => void;
  /** Вызывается при выборе знаменитости (id и имя для API по имени) */
  onCelebritySelect?: (option: {
    value: number;
    label: string;
  }) => void;
  /** Текущая сортировка по имени */
  sortByName?: 'asc' | 'desc';
  /** Установка сортировки по имени */
  setSortByName?: (value: 'asc' | 'desc' | undefined) => void;
}
const renderFilterInputRow = (t: ReturnType<typeof useTranslations<'Celebrities.OurCelebrities.Filter'>>, props: {
  celebrities?: number[];
  onCelebritiesChange?: (ids: number[]) => void;
  celebrityName?: string;
  setCelebrityName?: (value: string) => void;
  onCelebritySelect?: (option: {
    value: number;
    label: string;
  }) => void;
  onClearAll?: () => void;
  className?: string;
}) => <div className={`flex w-full items-center gap-2 ${props.className ?? ''}`}>
    <CelebritySearchSelect value={props.celebrities ?? []} onChange={ids => props.onCelebritiesChange?.(ids)} onOptionSelect={option => props.onCelebritySelect?.(option)} placeholder={t('CelebrityPlaceholder')} className="flex-1 min-w-0" allowClear={true} searchValue={props.celebrityName ?? ''} setSearchValue={props.setCelebrityName} />
    <button type="button" className="ml-2 hidden shrink-0 items-center gap-2 pl-3 text-base text-[#A0A3E2] lg:flex" onClick={props.onClearAll}>
      <DeleteOutlined className="size-4" />
      <span className="text-base">{t('RemoveAllFilters')}</span>
    </button>
  </div>;
export const FilterCelebrities: FC<FilterCelebritiesProps> = t0 => {
  const $ = _c(115);
  const {
    celebrities: t1,
    onCelebritiesChange,
    celebrityName,
    setCelebrityName,
    onCelebritySelect,
    sortByName,
    setSortByName
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const celebrities = t2;
  const t = useTranslations("Celebrities.OurCelebrities.Filter");
  const isMobile = useMediaQuery("(max-width: 1024px)");
  const [isOpen, setIsOpen] = useState(false);
  const [localCelebrities, setLocalCelebrities] = useState(celebrities);
  const [localCelebrityName, setLocalCelebrityName] = useState(celebrityName ?? "");
  const [localSortByName, setLocalSortByName] = useState(sortByName);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = celebritiesListOptions({});
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: t4
  } = useQuery(t3);
  let t5;
  if ($[3] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const celebritiesList = t5;
  let t6;
  if ($[5] !== celebritiesList) {
    t6 = id => {
      const celebrity = celebritiesList.find(c => c.id === id);
      return celebrity ? `${celebrity.first_name} ${celebrity.last_name}`.trim() : "";
    };
    $[5] = celebritiesList;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const getCelebrityShortNameById = t6;
  let t7;
  bb0: {
    if (!celebrities?.length) {
      let t8;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t8 = [];
        $[7] = t8;
      } else {
        t8 = $[7];
      }
      t7 = t8;
      break bb0;
    }
    let t8;
    if ($[8] !== celebrities || $[9] !== getCelebrityShortNameById) {
      let t9;
      if ($[11] !== getCelebrityShortNameById) {
        t9 = value => ({
          key: "celebrities",
          value,
          label: getCelebrityShortNameById(value) || String(value)
        });
        $[11] = getCelebrityShortNameById;
        $[12] = t9;
      } else {
        t9 = $[12];
      }
      t8 = celebrities.map(t9);
      $[8] = celebrities;
      $[9] = getCelebrityShortNameById;
      $[10] = t8;
    } else {
      t8 = $[10];
    }
    t7 = t8;
  }
  const appliedFilters = t7;
  let t8;
  bb1: {
    if (!localCelebrities?.length) {
      let t9;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [];
        $[13] = t9;
      } else {
        t9 = $[13];
      }
      t8 = t9;
      break bb1;
    }
    let t9;
    if ($[14] !== getCelebrityShortNameById || $[15] !== localCelebrities) {
      let t10;
      if ($[17] !== getCelebrityShortNameById) {
        t10 = value_0 => ({
          key: "celebrities",
          value: value_0,
          label: getCelebrityShortNameById(value_0) || String(value_0)
        });
        $[17] = getCelebrityShortNameById;
        $[18] = t10;
      } else {
        t10 = $[18];
      }
      t9 = localCelebrities.map(t10);
      $[14] = getCelebrityShortNameById;
      $[15] = localCelebrities;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    t8 = t9;
  }
  const localAppliedFilters = t8;
  let t10;
  let t9;
  if ($[19] !== celebrities || $[20] !== celebrityName || $[21] !== sortByName) {
    t9 = () => {
      setLocalCelebrities(celebrities);
      setLocalCelebrityName(celebrityName ?? "");
      setLocalSortByName(sortByName);
    };
    t10 = [celebrities, celebrityName, sortByName];
    $[19] = celebrities;
    $[20] = celebrityName;
    $[21] = sortByName;
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[24] !== isMobile || $[25] !== isOpen) {
    t11 = () => {
      if (isMobile && isOpen) {
        const scrollY = window.scrollY;
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";
      } else {
        if (isMobile) {
          const scrollY_0 = document.body.style.top;
          document.body.style.position = "";
          document.body.style.top = "";
          document.body.style.width = "";
          document.body.style.overflow = "";
          if (scrollY_0) {
            window.scrollTo(0, parseInt(scrollY_0 || "0", 10) * -1);
          }
        }
      }
    };
    t12 = [isMobile, isOpen];
    $[24] = isMobile;
    $[25] = isOpen;
    $[26] = t11;
    $[27] = t12;
  } else {
    t11 = $[26];
    t12 = $[27];
  }
  useEffect(t11, t12);
  let t13;
  if ($[28] !== onCelebritiesChange || $[29] !== setCelebrityName || $[30] !== setSortByName) {
    t13 = () => {
      onCelebritiesChange?.([]);
      setCelebrityName?.("");
      setSortByName?.(undefined);
    };
    $[28] = onCelebritiesChange;
    $[29] = setCelebrityName;
    $[30] = setSortByName;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const handleClearAll = t13;
  let t14;
  if ($[32] !== celebrities || $[33] !== celebrityName || $[34] !== sortByName) {
    t14 = () => {
      setLocalCelebrities(celebrities);
      setLocalCelebrityName(celebrityName ?? "");
      setLocalSortByName(sortByName);
      setIsOpen(false);
    };
    $[32] = celebrities;
    $[33] = celebrityName;
    $[34] = sortByName;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const handleMobileCancel = t14;
  let t15;
  if ($[36] !== localCelebrities || $[37] !== localCelebrityName || $[38] !== localSortByName || $[39] !== onCelebritiesChange || $[40] !== setCelebrityName || $[41] !== setSortByName) {
    t15 = () => {
      onCelebritiesChange?.(localCelebrities);
      setCelebrityName?.(localCelebrityName);
      setSortByName?.(localSortByName);
      setIsOpen(false);
    };
    $[36] = localCelebrities;
    $[37] = localCelebrityName;
    $[38] = localSortByName;
    $[39] = onCelebritiesChange;
    $[40] = setCelebrityName;
    $[41] = setSortByName;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  const handleMobileApply = t15;
  if (isMobile) {
    let t16;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = () => setIsOpen(true);
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    let t17;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <img src="/icons/filter.svg" alt="Filter" className="size-4" />;
      $[44] = t17;
    } else {
      t17 = $[44];
    }
    let t18;
    if ($[45] !== t) {
      t18 = t("Title");
      $[45] = t;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    let t19;
    if ($[47] !== t18) {
      t19 = <button type="button" onClick={t16} className="flex h-10 items-center gap-2 text-sm text-[#A0A3E2]">{t17}{t18}</button>;
      $[47] = t18;
      $[48] = t19;
    } else {
      t19 = $[48];
    }
    let t20;
    if ($[49] !== setSortByName) {
      t20 = t21 => {
        const {
          sortBy
        } = t21;
        return setSortByName?.(sortBy);
      };
      $[49] = setSortByName;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    let t21;
    if ($[51] !== t) {
      t21 = t("SortOptions.NameAsc");
      $[51] = t;
      $[52] = t21;
    } else {
      t21 = $[52];
    }
    let t22;
    if ($[53] !== t21) {
      t22 = <SortRadioButton sortBy="asc" disabledSortTypeChange={true}>{t21}</SortRadioButton>;
      $[53] = t21;
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    let t23;
    if ($[55] !== t) {
      t23 = t("SortOptions.NameDesc");
      $[55] = t;
      $[56] = t23;
    } else {
      t23 = $[56];
    }
    let t24;
    if ($[57] !== t23) {
      t24 = <SortRadioButton sortBy="desc" disabledSortTypeChange={true}>{t23}</SortRadioButton>;
      $[57] = t23;
      $[58] = t24;
    } else {
      t24 = $[58];
    }
    let t25;
    if ($[59] !== sortByName || $[60] !== t20 || $[61] !== t22 || $[62] !== t24) {
      t25 = <SortRadioGroup className="shrink-0" sortBy={sortByName} onSortChange={t20} showAll={true}>{t22}{t24}</SortRadioGroup>;
      $[59] = sortByName;
      $[60] = t20;
      $[61] = t22;
      $[62] = t24;
      $[63] = t25;
    } else {
      t25 = $[63];
    }
    let t26;
    if ($[64] !== t19 || $[65] !== t25) {
      t26 = <div className="flex flex-row items-center justify-between gap-4">{t19}{t25}</div>;
      $[64] = t19;
      $[65] = t25;
      $[66] = t26;
    } else {
      t26 = $[66];
    }
    let t27;
    if ($[67] !== handleMobileApply || $[68] !== handleMobileCancel || $[69] !== isOpen || $[70] !== localAppliedFilters || $[71] !== localCelebrities || $[72] !== localCelebrityName || $[73] !== t) {
      t27 = isOpen && <motion.div initial={{
        opacity: 0,
        y: "100%"
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: "100%"
      }} transition={{
        duration: 0.3
      }} className="fixed bottom-0 left-0 right-0 top-[32px] z-[9997] flex flex-col gap-2 bg-[#100D32] p-4"><div className="flex h-full flex-col justify-between"><div className="flex flex-col grow shrink overflow-hidden"><div className="mb-6 flex items-center justify-between"><span className="flex h-10 items-center gap-3 px-2 font-['Montserrat'] text-xl font-semibold text-white"><img src="/icons/filter.svg" alt="Filter" className="size-6" />{t("Title")}</span><button type="button" className="flex items-center gap-2 pl-3 pr-2 text-base text-[#A0A3E2]" onClick={() => {
                setLocalCelebrities([]);
                setLocalCelebrityName("");
                setLocalSortByName(undefined);
              }}><DeleteOutlined className="size-4" /><span className="text-base">{t("RemoveAllFilters")}</span></button></div><div className={clsx("mb-4 flex flex-col gap-4", styles.mobileFilterContent)}>{renderFilterInputRow(t, {
                celebrities: localCelebrities,
                onCelebritiesChange: setLocalCelebrities,
                celebrityName: localCelebrityName,
                setCelebrityName: setLocalCelebrityName,
                onCelebritySelect: option => {
                  setLocalCelebrities([option.value]);
                  setLocalCelebrityName(option.label);
                },
                className: "flex-col"
              })}<AppliedFilters filters={localAppliedFilters} onRemove={filter => setLocalCelebrities(prev => prev.filter(id_0 => id_0 !== filter.value))} /></div></div><div className="mt-4 flex flex-row gap-2"><button type="button" onClick={handleMobileCancel} className="h-[36px] grow rounded-lg border-2 border-white/10 font-['Montserrat'] text-xs font-bold uppercase text-white">{t("Cancel")}</button><button type="button" onClick={handleMobileApply} className="h-[36px] grow rounded-lg bg-white font-['Montserrat'] text-xs font-bold uppercase text-[#392f99]">{t("Apply")}</button></div></div></motion.div>;
      $[67] = handleMobileApply;
      $[68] = handleMobileCancel;
      $[69] = isOpen;
      $[70] = localAppliedFilters;
      $[71] = localCelebrities;
      $[72] = localCelebrityName;
      $[73] = t;
      $[74] = t27;
    } else {
      t27 = $[74];
    }
    let t28;
    if ($[75] !== t27) {
      t28 = <AnimatePresence>{t27}</AnimatePresence>;
      $[75] = t27;
      $[76] = t28;
    } else {
      t28 = $[76];
    }
    let t29;
    if ($[77] !== t26 || $[78] !== t28) {
      t29 = <div className="flex w-full flex-col gap-4 lg:gap-6">{t26}{t28}</div>;
      $[77] = t26;
      $[78] = t28;
      $[79] = t29;
    } else {
      t29 = $[79];
    }
    return t29;
  }
  let t16;
  if ($[80] !== celebrities || $[81] !== celebrityName || $[82] !== handleClearAll || $[83] !== onCelebritiesChange || $[84] !== onCelebritySelect || $[85] !== setCelebrityName || $[86] !== t) {
    t16 = renderFilterInputRow(t, {
      celebrities,
      onCelebritiesChange,
      celebrityName,
      setCelebrityName,
      onCelebritySelect,
      onClearAll: handleClearAll
    });
    $[80] = celebrities;
    $[81] = celebrityName;
    $[82] = handleClearAll;
    $[83] = onCelebritiesChange;
    $[84] = onCelebritySelect;
    $[85] = setCelebrityName;
    $[86] = t;
    $[87] = t16;
  } else {
    t16 = $[87];
  }
  let t17;
  if ($[88] !== setSortByName) {
    t17 = t18 => {
      const {
        sortBy: sortBy_0
      } = t18;
      return setSortByName?.(sortBy_0);
    };
    $[88] = setSortByName;
    $[89] = t17;
  } else {
    t17 = $[89];
  }
  let t18;
  if ($[90] !== t) {
    t18 = t("SortOptions.NameAsc");
    $[90] = t;
    $[91] = t18;
  } else {
    t18 = $[91];
  }
  let t19;
  if ($[92] !== t18) {
    t19 = <SortRadioButton sortBy="asc" disabledSortTypeChange={true}>{t18}</SortRadioButton>;
    $[92] = t18;
    $[93] = t19;
  } else {
    t19 = $[93];
  }
  let t20;
  if ($[94] !== t) {
    t20 = t("SortOptions.NameDesc");
    $[94] = t;
    $[95] = t20;
  } else {
    t20 = $[95];
  }
  let t21;
  if ($[96] !== t20) {
    t21 = <SortRadioButton sortBy="desc" disabledSortTypeChange={true}>{t20}</SortRadioButton>;
    $[96] = t20;
    $[97] = t21;
  } else {
    t21 = $[97];
  }
  let t22;
  if ($[98] !== sortByName || $[99] !== t17 || $[100] !== t19 || $[101] !== t21) {
    t22 = <SortRadioGroup className="shrink-0" sortBy={sortByName} onSortChange={t17} showAll={true}>{t19}{t21}</SortRadioGroup>;
    $[98] = sortByName;
    $[99] = t17;
    $[100] = t19;
    $[101] = t21;
    $[102] = t22;
  } else {
    t22 = $[102];
  }
  let t23;
  if ($[103] !== celebrities || $[104] !== onCelebritiesChange) {
    t23 = filter_0 => onCelebritiesChange?.(celebrities.filter(id_1 => id_1 !== filter_0.value));
    $[103] = celebrities;
    $[104] = onCelebritiesChange;
    $[105] = t23;
  } else {
    t23 = $[105];
  }
  let t24;
  if ($[106] !== appliedFilters || $[107] !== t23) {
    t24 = <AppliedFilters className="shrink-1" filters={appliedFilters} onRemove={t23} />;
    $[106] = appliedFilters;
    $[107] = t23;
    $[108] = t24;
  } else {
    t24 = $[108];
  }
  let t25;
  if ($[109] !== t22 || $[110] !== t24) {
    t25 = <div className="mb-9 flex gap-16 items-center">{t22}{t24}</div>;
    $[109] = t22;
    $[110] = t24;
    $[111] = t25;
  } else {
    t25 = $[111];
  }
  let t26;
  if ($[112] !== t16 || $[113] !== t25) {
    t26 = <div className="flex w-full flex-col gap-4 lg:gap-6">{t16}{t25}</div>;
    $[112] = t16;
    $[113] = t25;
    $[114] = t26;
  } else {
    t26 = $[114];
  }
  return t26;
};